import React, {useEffect, useRef, useState} from 'react';
import {SelectOption} from '../../../data/models/UI/SelectOption';
import {NA_OPTION} from '../../../data/staticValues/selectOptions';
import './styles.css';
import SearchInput from '../SearchInput';

type DropdownType = 'standart' | 'box';

interface Props {
  heading?: string;
  options: SelectOption[];
  placeholder?: string;
  selectedOption?: string | null;
  setSelectedOption: (option: string) => void;
  className?: string;
  withNA?: boolean;
  fieldError?: boolean;
  disabled?: boolean;
  isMandatory?: boolean;
  type?: DropdownType;
  subtitle?: string;
  onDropdownVisibilityToggle?: (isVisible: boolean) => void;
  enableSearch?: boolean;
}

const Dropdown = React.memo(
  ({
    heading,
    options,
    placeholder,
    selectedOption,
    setSelectedOption,
    className,
    withNA,
    fieldError,
    disabled,
    isMandatory,
    type,
    subtitle,
    onDropdownVisibilityToggle,
    enableSearch,
  }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const openerRef = useRef<any>(null);
    const openerTextRef = useRef<any>(null);
    const openerArrowRef = useRef<any>(null);

    useEffect(() => {
      window.addEventListener('click', handleBodyClick);
      return () => {
        window.removeEventListener('click', handleBodyClick);
      };
    }, [isOpen]);

    const handleBodyClick = (ev: any) => {
      if (
        isOpen &&
        openerRef?.current !== ev.target &&
        openerTextRef?.current !== ev.target &&
        openerArrowRef?.current !== ev.target
      ) {
        toggle();
      }
    };

    const getOptions = () => {
      let result = options;
      if (withNA) {
        result = [NA_OPTION, ...options];
      }
      if (enableSearch) {
        return result.filter(x =>
          x.value.toLowerCase().includes(search.toLowerCase()),
        );
      } else {
        return result;
      }
    };

    const toggle = () => {
      if (!disabled) {
        if (onDropdownVisibilityToggle) {
          onDropdownVisibilityToggle(!isOpen);
        }
        setIsOpen(!isOpen);
      }
    };

    const onOptionClicked = (value: string) => () => {
      setSelectedOption(value);
      setIsOpen(false);
      if (onDropdownVisibilityToggle) {
        onDropdownVisibilityToggle(false);
      }
    };

    const getSelectedOption = () => {
      return getOptions().find(option => option.key === selectedOption)?.value;
    };

    const getRootClassByType = () => {
      switch (type) {
        case 'box':
          return 'DropdownBodyBox';
        default:
          return 'DropdownBody';
      }
    };

    return (
      <div className={`${className}`}>
        {heading && (
          <p className={`DropdownTitle ${disabled && 'DropdownDisabled'}`}>
            {heading}
            {isMandatory && !disabled ? (
              <span className="MandatoryIndicator">*</span>
            ) : (
              ''
            )}
          </p>
        )}
        {subtitle && <p className="DropdownSubtitleText">{subtitle}</p>}
        <div className="DropdownContainer">
          <div
            ref={openerRef}
            className={`${getRootClassByType()} ${
              !selectedOption ? 'Placeholder' : ''
            } ${fieldError ? 'FieldError' : ''} ${
              disabled && 'DropdownDisabled'
            }`}
            onClick={toggle}>
            <span ref={openerTextRef}>
              {getSelectedOption() || placeholder}
            </span>
            <span
              ref={openerArrowRef}
              className={
                isOpen ? 'icon-chevron-up' : 'icon-chevron-down'
              }></span>
          </div>
          {isOpen && (
            <ul
              onClick={e => e.stopPropagation()}
              className={`DropdownList ${
                enableSearch ? 'DropdownListWithSearch' : ''
              }`}>
              {enableSearch && (
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  className="DropdownSearch"
                />
              )}
              {getOptions().map(option => (
                <li
                  className="ListItem"
                  onClick={onOptionClicked(option.key)}
                  key={option.key}>
                  {option.value}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  },
);

export default Dropdown;
